/*
 * Copyright © 2026 Medaica, Inc
 *
 * All rights reserved.
 *
 * This code is confidential and proprietary information belonging to Medaica, Inc.
 * Unauthorized copying, distribution, or use of this code, in whole or in part,
 * is strictly prohibited, and may constitute a violation of intellectual property rights.
 *
 * If you have received this code in error, please notify the owner immediately
 * at support@medaica.com and delete this file from your system.
 */

import React, { ReactElement } from "react"
import { observer } from "mobx-react-lite"
import { Form, Formik, FormikValues, useFormikContext } from "formik"
import * as Yup from "yup"
import { Button } from "@mui/material"
import TextInput from "@medaica/common/components/forms/text-input"
import { useBookingStores } from "../booking-context"
import WizardStepLayout from "../components/wizard-step-layout"
import DateOfBirthField from "../components/date-of-birth-field"
import GenderSelect, { SelectOption } from "../components/gender-select"
import bookingCopy from "../bookingCopy"
import { bookingColors, footerButtonSx } from "../booking-theme"

const copy = bookingCopy.patient

// Same values the native <select> submitted — the custom dropdown only restyles the control.
const GENDER_OPTIONS: SelectOption[] = [
  { value: "male", label: copy.genderOptions.male },
  { value: "female", label: copy.genderOptions.female },
  { value: "unknown", label: copy.genderOptions.unknown },
]

// One uniform field skin (taller, softer corners) for all six fields. Text inputs get their
// horizontal padding from @tailwindcss/forms; the button triggers don't, so theirs adds px-3.
const FIELD_CLASS = "rounded-lg py-3"
const TRIGGER_CLASS = "rounded-lg px-3 py-3"

// Self-booking only for now — the "Someone else" path (and its subtitle/emailNote copy) is hidden
// behind this flag. Flip to true to restore booking on another person's behalf.
const ALLOW_BOOKING_FOR_OTHERS = false

const isAdult = (value?: string): boolean => {
  // Empty is the `.required()` validator's job — pass here so a blank field shows
  // "Required", not the 18+ message. Presence is still gated (required blocks submit).
  if (!value) {
    return true
  }
  // Parse the YYYY-MM-DD parts into a LOCAL date; new Date("YYYY-MM-DD") is UTC
  // midnight, which combined with local getters shifts the day back one in
  // negative-UTC-offset zones and lets a 17-year-old pass the 18+ gate (D4).
  const parts = value.split("-").map(Number)
  if (parts.length !== 3 || parts.some(Number.isNaN)) {
    return false
  }
  const [year, month, day] = parts
  const eighteenthBirthday = new Date(year + 18, month - 1, day)
  return eighteenthBirthday <= new Date()
}

// No-dependency phone sanity check: allowed characters only (digits, spaces, and + ( ) . -)
// with 7–15 digits (E.164's range). Not full libphonenumber validation, but it rejects junk
// (letters, too few digits) while accepting formatted US and international +numbers. Empty is
// the `.required()` validator's job — pass here so a blank field shows "Required".
const PHONE_PATTERN = /^\+?[\d\s().-]+$/
const isValidPhone = (value?: string): boolean => {
  if (!value) {
    return true
  }
  const digits = value.replace(/\D/g, "")
  return PHONE_PATTERN.test(value) && digits.length >= 7 && digits.length <= 15
}

const validationSchema = Yup.object({
  firstName: Yup.string().trim().required(copy.errors.required),
  lastName: Yup.string().trim().required(copy.errors.required),
  dateOfBirth: Yup.string().required(copy.errors.required).test("adult", copy.errors.underage, isAdult),
  gender: Yup.string().required(copy.errors.required),
  email: Yup.string().trim().email(copy.errors.email).required(copy.errors.required),
  phone: Yup.string().trim().required(copy.errors.required).test("phone", copy.errors.phone, isValidPhone),
})

// A thin binding of the shared TextInput to the surrounding Formik context, so the
// six fields below stay short and readable.
const PatientTextField = ({
  name,
  label,
  type,
  helpText,
}: {
  name: string
  label: string
  type: string
  helpText?: string
}): ReactElement => {
  const { errors, touched, handleChange, handleBlur } = useFormikContext<FormikValues>()
  return (
    <TextInput
      label={label}
      name={name}
      type={type}
      helpText={helpText}
      className={FIELD_CLASS}
      errors={errors}
      touched={touched}
      onChange={handleChange}
      onBlur={handleBlur}
    />
  )
}

const Patient = observer((): ReactElement => {
  const { sessionStore, uiStore, wizardStore } = useBookingStores()
  const account = sessionStore.account
  const buyerFirstName = account?.buyerFirstName ?? ""
  const buyerLastName = account?.buyerLastName ?? ""
  const buyerEmail = account?.buyerEmail ?? ""
  const buyerPhone = account?.buyerPhone ?? ""
  const examsAvailable = account?.examsAvailable ?? 0
  const isMe = wizardStore.forWhom === "me"

  // Self-booking (buyer = patient): prefill the form from the verified buyer account. The
  // "someone else" path never seeds patient fields from the buyer. Each buyer field falls
  // back to "" until the backend supplies it, so this degrades gracefully.
  const initialValues = {
    forWhom: wizardStore.forWhom,
    firstName: wizardStore.patient?.firstName ?? (isMe ? buyerFirstName : ""),
    lastName: wizardStore.patient?.lastName ?? (isMe ? buyerLastName : ""),
    dateOfBirth: wizardStore.patient?.dateOfBirth ?? "",
    gender: wizardStore.patient?.gender ?? "",
    email: wizardStore.patient?.email ?? (isMe ? buyerEmail : ""),
    phone: wizardStore.patient?.phone ?? (isMe ? buyerPhone : ""),
  }

  return (
    <WizardStepLayout current="patient" headerRight={bookingCopy.examsAvailable(examsAvailable)}>
      <h1 className="text-2xl font-bold" style={{ color: bookingColors.primary }}>
        {copy.title}
      </h1>
      <p className="mt-1 mb-6 text-gray-600">{copy.subtitle}</p>

      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={(values) => {
          wizardStore.setForWhom(values.forWhom)
          wizardStore.setPatient({
            firstName: values.firstName.trim(),
            lastName: values.lastName.trim(),
            dateOfBirth: values.dateOfBirth,
            gender: values.gender,
            email: values.email.trim(),
            phone: values.phone.trim(),
          })
          uiStore.goTo("consent")
        }}
      >
        {({ values, setFieldValue }) => (
          <Form>
            <div className="bg-white rounded-lg shadow-sm p-8 space-y-5">
              {/* Self-booking only: the who-is-this-for chooser stays in the flow (forWhom defaults
                  to "me", prefilling the buyer's name) but is hidden until "Someone else" returns. */}
              {ALLOW_BOOKING_FOR_OTHERS ? (
                <div className="flex gap-6">
                  <label className="flex items-center gap-2">
                    <input
                      type="radio"
                      name="forWhom"
                      checked={values.forWhom === "me"}
                      onChange={() => {
                        void setFieldValue("forWhom", "me")
                        void setFieldValue("firstName", buyerFirstName)
                        void setFieldValue("lastName", buyerLastName)
                        void setFieldValue("email", buyerEmail)
                        void setFieldValue("phone", buyerPhone)
                      }}
                    />
                    <span>{copy.me(buyerFirstName || "me")}</span>
                  </label>
                  <label className="flex items-center gap-2">
                    <input
                      type="radio"
                      name="forWhom"
                      checked={values.forWhom === "someoneElse"}
                      onChange={() => {
                        void setFieldValue("forWhom", "someoneElse")
                        void setFieldValue("firstName", "")
                        void setFieldValue("lastName", "")
                        void setFieldValue("email", "")
                        void setFieldValue("phone", "")
                      }}
                    />
                    <span>{copy.someoneElse}</span>
                  </label>
                </div>
              ) : null}

              {/* Packed 2-col grid: First/Last · DOB/Gender · Email/Phone. Each cell stacks
                  label → input → error/help. DOB + Gender are custom widgets that emit the same
                  values (YYYY-MM-DD, male/female/unknown) the native controls did. */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-6">
                <div>
                  <PatientTextField name="firstName" label={copy.firstName} type="text" />
                </div>
                <div>
                  <PatientTextField name="lastName" label={copy.lastName} type="text" />
                </div>
                <DateOfBirthField
                  name="dateOfBirth"
                  label={copy.dateOfBirth}
                  placeholder={copy.selectPlaceholder}
                  className={TRIGGER_CLASS}
                />
                <GenderSelect
                  name="gender"
                  label={copy.gender}
                  placeholder={copy.selectPlaceholder}
                  options={GENDER_OPTIONS}
                  className={TRIGGER_CLASS}
                />
                {/* Helper lines are rendered locally (not via TextInput's helpText) so they can be
                    smaller and quieter than the shared component's default — same copy. */}
                <div>
                  <PatientTextField name="email" label={copy.email} type="email" />
                  <p className="mt-2 text-xs text-gray-500">{copy.emailHelp}</p>
                </div>
                <div>
                  <PatientTextField name="phone" label={copy.phone} type="tel" />
                  <p className="mt-2 text-xs text-gray-500">{copy.phoneHelp}</p>
                </div>
              </div>
            </div>

            <div className="mt-6 flex items-center justify-between">
              <Button variant="outlined" color="inherit" sx={footerButtonSx} onClick={() => uiStore.goTo("account")}>
                {copy.back}
              </Button>
              <Button variant="contained" type="submit" sx={footerButtonSx}>
                {copy.continueButton}
              </Button>
            </div>
          </Form>
        )}
      </Formik>
    </WizardStepLayout>
  )
})

export default Patient
